import type { StudioMode, Transition } from './scene';
import { DEFAULT_TRANSITION } from './scene';

/**
 * What a Live is *doing*, as opposed to what it is made of.
 *
 * The scenes are the composition and live in `lives.scene_override`. This is
 * the session around them: which scene is on air, which the director was
 * editing, and the sources that were feeding it. A refresh or a dropped
 * connection must not cost any of that - the room is still there, the guests
 * are still there, and coming back to an empty studio is the difference
 * between an interruption and a lost broadcast.
 *
 * It is stored on the Live and never on the Show. A Show is a template: what
 * was on air at 21:04 on one night is not part of it.
 */

/** How a source was acquired, which is the only thing that differs on restore. */
export const SOURCE_KINDS = ['device', 'file', 'youtube'] as const;
export type SourceKind = (typeof SOURCE_KINDS)[number];

/**
 * Enough to put a source back, and never the source itself.
 *
 * None of these can hold a live handle: a `MediaStreamTrack` is not
 * serialisable, and a `File` does not survive the page that opened it. What
 * survives is the *identity* of the source, so the studio can come back
 * knowing a clip called "sigla.mp4" was the second media source and is missing
 * its file, rather than knowing nothing at all.
 */
export interface SourceManifestEntry {
  id: string;
  /**
   * The number in the LiveKit track name (`cam-2`, `media-3`).
   *
   * **This is the load-bearing field.** A layer points at a track by name, so
   * a restored source that comes back as `media-1` when it was `media-2` puts
   * the wrong clip in the frame. Indices are therefore restored with the
   * source and never renumbered, and a new source takes one above the highest
   * this manifest has ever used.
   */
  index: number;
  kind: SourceKind;
  name: string;
  /** Device sources: what to reacquire. Stable per origin once granted. */
  videoDeviceId: string | null;
  audioDeviceId: string | null;
  /**
   * A file source: its name, which is all a browser is allowed to remember.
   *
   * The bytes are gone with the page, so a restored file source exists,
   * carries its name, and holds every position it had in every scene - but has
   * no picture until the director points it at the file again.
   */
  fileName: string | null;
  /** A YouTube clip: the id, which is the whole source. */
  videoId: string | null;
}

export interface StudioState {
  mode: StudioMode;
  /** The scene the programme was taken from, and what is restored to air. */
  activeSceneId: string | null;
  /** The scene the director was editing. */
  previewSceneId: string | null;
  transition: Transition;
  sources: SourceManifestEntry[];
}

export const DEFAULT_STUDIO_STATE: StudioState = {
  mode: 'studio',
  activeSceneId: null,
  previewSceneId: null,
  transition: DEFAULT_TRANSITION,
  sources: [],
};

/**
 * The next free track index.
 *
 * Taken from the highest the manifest holds rather than its length, so
 * removing a source cannot hand its number to the next one and quietly point
 * an existing layer at a different feed.
 */
export function nextSourceIndex(sources: SourceManifestEntry[]): number {
  return sources.reduce((highest, source) => Math.max(highest, source.index), 0) + 1;
}
